/**
 * Anonymous attempt persistence via localStorage.
 * Every read is validated. Every write is guarded against quota errors.
 * Versioned under `typingwhiz:attempts:v1`.
 *
 * @module lib/storage/attempts
 */
import { z } from 'zod';

const STORAGE_KEY = 'typingwhiz:attempts:v1';
const MAX_ATTEMPTS = 100;

const AttemptSchema = z.object({
  id: z.string().min(1),
  activityType: z.enum(['typing_test', 'lesson', 'practice', 'story', 'game']),
  activityRefSlug: z.string().nullable(),
  durationSeconds: z.number().int().positive(),
  grossWpm: z.number().nonnegative(),
  netWpm: z.number().nonnegative(),
  accuracy: z.number().min(0).max(100),
  correctChars: z.number().int().nonnegative(),
  incorrectChars: z.number().int().nonnegative(),
  totalChars: z.number().int().nonnegative(),
  backspaces: z.number().int().nonnegative().optional().default(0),
  createdAt: z.string().datetime(),
});

const StoreSchema = z.object({
  version: z.literal(1),
  attempts: z.array(AttemptSchema).max(MAX_ATTEMPTS),
});

/**
 * @typedef {z.infer<typeof AttemptSchema>} StoredAttempt
 */

/**
 * Safe access to localStorage. Returns null if unavailable.
 * @returns {Storage | null}
 */
function getStorage() {
  if (typeof window === 'undefined') return null;
  try {
    const test = '__tw_test__';
    window.localStorage.setItem(test, '1');
    window.localStorage.removeItem(test);
    return window.localStorage;
  } catch {
    return null;
  }
}

/**
 * Reads all stored attempts. Returns [] on any error.
 * @returns {StoredAttempt[]}
 */
export function readAttempts() {
  const storage = getStorage();
  if (!storage) return [];
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    const result = StoreSchema.safeParse(parsed);
    if (!result.success) {
      // Corrupted or incompatible version: discard silently.
      storage.removeItem(STORAGE_KEY);
      return [];
    }
    return result.data.attempts;
  } catch {
    return [];
  }
}

/**
 * Appends an attempt. Keeps only the most recent MAX_ATTEMPTS.
 * @param {Omit<StoredAttempt, 'id' | 'createdAt'>} attempt
 * @returns {StoredAttempt | null} The stored record, or null on failure.
 */
export function appendAttempt(attempt) {
  const storage = getStorage();
  if (!storage) return null;

  const record = {
    ...attempt,
    id:
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `a_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
    createdAt: new Date().toISOString(),
  };

  const existing = readAttempts();
  const next = [record, ...existing].slice(0, MAX_ATTEMPTS);

  try {
    storage.setItem(
      STORAGE_KEY,
      JSON.stringify({ version: 1, attempts: next })
    );
    return record;
  } catch (e) {
    // Quota exceeded or private browsing write block. Non-fatal.
    console.warn('Failed to save attempt locally:', e.message);
    return null;
  }
}

/**
 * Returns the best net WPM for a given activity type and optional duration.
 * @param {string} activityType
 * @param {number} [durationSeconds] - Restrict to attempts of this exact duration.
 * @returns {StoredAttempt | null}
 */
export function getPersonalBest(activityType, durationSeconds) {
  const attempts = readAttempts().filter(
    (a) => a.activityType === activityType
  );
  const filtered = durationSeconds
    ? attempts.filter((a) => a.durationSeconds === durationSeconds)
    : attempts;
  if (filtered.length === 0) return null;
  return filtered.reduce((best, a) => (a.netWpm > best.netWpm ? a : best));
}

/**
 * Returns the most recent attempts for a given activity type.
 * @param {string} activityType
 * @param {number} [limit=5]
 * @returns {StoredAttempt[]}
 */
export function getRecentAttempts(activityType, limit = 5) {
  return readAttempts()
    .filter((a) => a.activityType === activityType)
    .slice(0, limit);
}

/**
 * Clears all local attempts. User-initiated.
 */
export function clearAttempts() {
  const storage = getStorage();
  if (storage) storage.removeItem(STORAGE_KEY);
}

/**
 * Returns total attempt count for a given activity type.
 * @param {string} activityType
 * @returns {number}
 */
export function countAttempts(activityType) {
  return readAttempts().filter((a) => a.activityType === activityType).length;
}